import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { find, remove } from "lodash"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { toaster } from "components/common/Toast"

const transformAssignedCrewData = (assignment) => {
  if (!assignment) return assignment

  return {
    id: assignment.id,
    tripBookingId: assignment.trip_booking_id,
    profileId: assignment.profile_id,
    assignmentRole: assignment.assignment_role,
    inheritedFromTrip: assignment.inherited_from_trip,
    assignedById: assignment.assigned_by_id,
    profileName: assignment.profile_name,
    assignedByName: assignment.assigned_by_name,
    createdAt: assignment.created_at,
    updatedAt: assignment.updated_at,
    profile: assignment.profile
      ? {
          id: assignment.profile.id,
          firstName: assignment.profile.first_name,
          lastName: assignment.profile.last_name,
          fullName: assignment.profile.full_name,
          email: assignment.profile.email,
          phone: assignment.profile.phone
        }
      : null
  }
}

const transformAvailableStaffData = (staff, status = "available") => {
  if (!staff) return staff

  let roles = []
  if (staff.roles && Array.isArray(staff.roles)) {
    roles = staff.roles
  } else if (staff.role) {
    roles = [staff.role]
  } else if (staff.primary_role) {
    roles = [{ name: staff.primary_role }]
  }

  return {
    id: staff.id,
    firstName: staff.first_name,
    lastName: staff.last_name,
    fullName: staff.full_name,
    email: staff.email,
    phone: staff.phone,
    userId: staff.user_id,
    guideId: staff.guide_id,
    avatarUrl: staff.avatar_url,
    roles,
    status
  }
}

const requestedAssignedCrew = createAction("staffAssignments/assignedCrew/requested")
const receivedAssignedCrew = createAction("staffAssignments/assignedCrew/received")
const failedAssignedCrew = createAction("staffAssignments/assignedCrew/failed")

const requestedAvailableStaff = createAction("staffAssignments/availableStaff/requested")
const receivedAvailableStaff = createAction("staffAssignments/availableStaff/received")
const failedAvailableStaff = createAction("staffAssignments/availableStaff/failed")

const requestedAssignStaff = createAction("staffAssignments/assign/requested")
const receivedAssignStaff = createAction("staffAssignments/assign/received")
const failedAssignStaff = createAction("staffAssignments/assign/failed")

const requestedUnassignStaff = createAction("staffAssignments/unassign/requested")
const receivedUnassignStaff = createAction("staffAssignments/unassign/received")
const failedUnassignStaff = createAction("staffAssignments/unassign/failed")

const requestedUpdateRole = createAction("staffAssignments/updateRole/requested")
const receivedUpdateRole = createAction("staffAssignments/updateRole/received")
const failedUpdateRole = createAction("staffAssignments/updateRole/failed")

const requestedConflicts = createAction("staffAssignments/conflicts/requested")
const receivedConflicts = createAction("staffAssignments/conflicts/received")
const failedConflicts = createAction("staffAssignments/conflicts/failed")

const initialState = {
  assignedCrew: {
    ...LOADING_INITIAL_STATE,
    items: [],
    summary: null
  },
  availableStaff: {
    ...LOADING_INITIAL_STATE,
    items: [],
    count: 0,
    // Per-booking cache so reopening a row's picker doesn't refetch the whole list.
    // Invalidated (deleted) whenever that booking's crew changes.
    byBooking: {},
    loadingByBooking: {}
  },
  conflicts: {
    ...LOADING_INITIAL_STATE,
    items: [],
    count: 0
  },
  assignOperation: {
    ...LOADING_INITIAL_STATE
  },
  unassignOperation: {
    ...LOADING_INITIAL_STATE
  },
  updateRoleOperation: {
    ...LOADING_INITIAL_STATE
  }
}

const staffAssignmentsReducer = createReducer(initialState, {
  [requestedAssignedCrew.type]: (state) => {
    setRequested(state.assignedCrew)
  },
  [receivedAssignedCrew.type]: (state, { payload }) => {
    setReceived(state.assignedCrew)
    const assignments = payload.data || []
    state.assignedCrew.items = assignments.map(transformAssignedCrewData)
    state.assignedCrew.summary = payload.summary || null
  },
  [failedAssignedCrew.type]: (state) => {
    setFailed(state.assignedCrew)
  },

  [requestedAvailableStaff.type]: (state, action) => {
    setRequested(state.availableStaff)
    const bookingId = action.props?.bookingId
    if (bookingId) state.availableStaff.loadingByBooking[bookingId] = true
  },
  [receivedAvailableStaff.type]: (state, action) => {
    const { payload, props } = action
    setReceived(state.availableStaff)
    const data = payload.data

    let items = []
    if (Array.isArray(data)) {
      items = data.map((s) => transformAvailableStaffData(s, "available"))
    } else if (data && typeof data === "object") {
      const available = (data.available || []).map((s) => transformAvailableStaffData(s, "available"))
      const unavailable = (data.unavailable || []).map((s) => transformAvailableStaffData(s, "unavailable"))
      const hasBookings = (data.has_bookings || []).map((s) => transformAvailableStaffData(s, "has_bookings"))
      items = [...available, ...unavailable, ...hasBookings]
    }

    state.availableStaff.items = items
    state.availableStaff.count = payload.count || items.length

    // Only cache the full (unfiltered) list — search results are transient
    const bookingId = props?.bookingId
    if (bookingId) {
      state.availableStaff.loadingByBooking[bookingId] = false
      if (!props?.search) state.availableStaff.byBooking[bookingId] = items
    }
  },
  [failedAvailableStaff.type]: (state, action) => {
    setFailed(state.availableStaff)
    const bookingId = action.props?.bookingId
    if (bookingId) state.availableStaff.loadingByBooking[bookingId] = false
  },

  [requestedAssignStaff.type]: (state) => {
    setRequested(state.assignOperation)
  },
  [receivedAssignStaff.type]: (state, action) => {
    const { payload, props } = action
    setReceived(state.assignOperation)
    const newAssignments = payload.data || []

    newAssignments.forEach((assignment) => {
      const transformedAssignment = transformAssignedCrewData(assignment)
      const existingIndex = state.assignedCrew.items.findIndex((item) => item.id === transformedAssignment.id)

      if (existingIndex === -1) {
        state.assignedCrew.items.push(transformedAssignment)
      }
      remove(state.availableStaff.items, (item) => item.id === assignment.profile_id)
    })
    if (payload.summary) {
      state.assignedCrew.summary = payload.summary
    }
    state.availableStaff.count = state.availableStaff.items.length

    // Crew changed for this booking — drop its cached available list so it refetches next open
    if (props?.bookingId) delete state.availableStaff.byBooking[props.bookingId]

    toaster.success({
      title: "Success",
      text: payload.message || "Staff member(s) assigned successfully"
    })
  },
  [failedAssignStaff.type]: (state) => {
    setFailed(state.assignOperation)
  },

  [requestedUnassignStaff.type]: (state) => {
    setRequested(state.unassignOperation)
  },
  [receivedUnassignStaff.type]: (state, action) => {
    const { payload, props } = action
    setReceived(state.unassignOperation)
    const assignmentId = props?.assignmentId ?? payload.assignmentId

    const removedAssignment = find(state.assignedCrew.items, ["id", assignmentId])
    if (removedAssignment) {
      remove(state.assignedCrew.items, ["id", assignmentId])
    }

    // Crew changed for this booking — invalidate its cached available list
    if (props?.bookingId) delete state.availableStaff.byBooking[props.bookingId]

    toaster.success({
      title: "Success",
      text: payload.message || "Staff member removed from booking"
    })
  },
  [failedUnassignStaff.type]: (state) => {
    setFailed(state.unassignOperation)
  },

  [requestedUpdateRole.type]: (state) => {
    setRequested(state.updateRoleOperation)
  },
  [receivedUpdateRole.type]: (state, { payload }) => {
    setReceived(state.updateRoleOperation)
    const updatedAssignment = payload.data

    const existingAssignment = find(state.assignedCrew.items, ["id", updatedAssignment.id])
    if (existingAssignment) {
      Object.assign(existingAssignment, transformAssignedCrewData(updatedAssignment))
    }

    toaster.success({
      title: "Success",
      text: payload.message || "Staff assignment updated"
    })
  },
  [failedUpdateRole.type]: (state) => {
    setFailed(state.updateRoleOperation)
  },

  [requestedConflicts.type]: (state) => {
    setRequested(state.conflicts)
  },
  [receivedConflicts.type]: (state, { payload }) => {
    setReceived(state.conflicts)
    state.conflicts.items = payload.data || []
    state.conflicts.count = payload.count || 0
  },
  [failedConflicts.type]: (state) => {
    setFailed(state.conflicts)
  }
})

export default staffAssignmentsReducer

export const getAssignedCrew = (bookingId) =>
  api({
    url: `/guide/bookings/${bookingId}/staff_assignments`,
    method: "get",
    onStart: requestedAssignedCrew.type,
    onSuccess: receivedAssignedCrew.type,
    onError: failedAssignedCrew.type
  })

export const getAvailableStaff = (bookingId, params = {}) =>
  api({
    url: `/guide/bookings/${bookingId}/staff_assignments/available_staff`,
    method: "get",
    params,
    onStart: requestedAvailableStaff.type,
    onSuccess: receivedAvailableStaff.type,
    onError: failedAvailableStaff.type,
    props: { bookingId, search: params.search || "" }
  })

export const assignStaffMember = (bookingId, data) =>
  api({
    url: `/guide/bookings/${bookingId}/staff_assignments`,
    method: "post",
    data,
    onStart: requestedAssignStaff.type,
    onSuccess: receivedAssignStaff.type,
    onError: failedAssignStaff.type
  })

export const assignMultipleStaff = (bookingId, staffAssignments) =>
  api({
    url: `/guide/bookings/${bookingId}/staff_assignments`,
    method: "post",
    data: { staff_assignments: staffAssignments },
    onStart: requestedAssignStaff.type,
    onSuccess: receivedAssignStaff.type,
    onError: failedAssignStaff.type,
    props: { bookingId }
  })

export const unassignStaffMember = (assignmentId, bookingId) =>
  api({
    url: `/guide/staff_assignments/${assignmentId}`,
    method: "delete",
    onStart: requestedUnassignStaff.type,
    onSuccess: receivedUnassignStaff.type,
    onError: failedUnassignStaff.type,
    props: { assignmentId, bookingId }
  })

export const updateStaffRole = (assignmentId, role) =>
  api({
    url: `/guide/staff_assignments/${assignmentId}`,
    method: "patch",
    data: { role },
    onStart: requestedUpdateRole.type,
    onSuccess: receivedUpdateRole.type,
    onError: failedUpdateRole.type
  })

export const getStaffConflicts = (bookingId) =>
  api({
    url: `/guide/bookings/${bookingId}/staff_assignments/conflicts`,
    method: "get",
    onStart: requestedConflicts.type,
    onSuccess: receivedConflicts.type,
    onError: failedConflicts.type
  })

export const selectAssignedCrew = (state) => state.staffAssignments.assignedCrew.items
export const selectAssignedCrewLoading = (state) => state.staffAssignments.assignedCrew.loading
export const selectAssignedCrewLoaded = (state) => state.staffAssignments.assignedCrew.loaded
export const selectAssignedCrewSummary = (state) => state.staffAssignments.assignedCrew.summary

export const selectAvailableStaff = (state) => state.staffAssignments.availableStaff.items
export const selectAvailableStaffLoading = (state) => state.staffAssignments.availableStaff.loading
export const selectAvailableStaffCount = (state) => state.staffAssignments.availableStaff.count

// Per-booking cache accessors
export const selectAvailableStaffForBooking = (bookingId) => (state) =>
  state.staffAssignments.availableStaff.byBooking[bookingId]
export const selectAvailableStaffLoadingForBooking = (bookingId) => (state) =>
  state.staffAssignments.availableStaff.loadingByBooking[bookingId] || false

export const selectStaffConflicts = (state) => state.staffAssignments.conflicts.items
export const selectStaffConflictsLoading = (state) => state.staffAssignments.conflicts.loading

export const selectAssignOperationLoading = (state) => state.staffAssignments.assignOperation.loading
export const selectUnassignOperationLoading = (state) => state.staffAssignments.unassignOperation.loading
export const selectUpdateRoleOperationLoading = (state) => state.staffAssignments.updateRoleOperation.loading
